<template>
  <div>
    <BusinessEntityInput
      v-model="companyName"
      :error="errorCompanyName"
      :label="$t('Registered company name')"
      name="company_name"
      :placeholder="$t('Full, registered company name, e.g. Miller Advertising GmbH')"
      required
      @focusout="v$.companyName.$touch"
    />

    <div class="tw-mt-4">
      <BusinessEntityInput
        v-model="companyNo"
        :error="errorCompanyNo"
        :label="$t('Company registration number')"
        name="company_no"
        :placeholder="$t('Registration number with the Handelsregister')"
        :required="requireCompanyNo"
        @focusout="v$.companyNo.$touch"
      />
    </div>

    <div class="tw-mt-4">
      <BusinessEntityInput
        v-model="vatNo"
        :error="errorVatNo"
        :label="$t('Tax number / UStID number (VAT no.)')"
        name="vat_no"
        placeholder="1441231234"
        required
        @focusout="v$.vatNo.$touch"
      />
    </div>

    <div class="tw-mt-5">
      <BusinessEntityIsVatRegistered v-model="isVatSubject" market="DE" name="is_vat_subject" />
    </div>
  </div>
</template>

<script>
import useVuelidate from '@vuelidate/core'
import { required, helpers } from '@vuelidate/validators'
import { computed, ref } from 'vue'
import BusinessEntityInput from '@/components/compliances/BusinessEntity/BusinessEntityInput.vue'
import BusinessEntityIsVatRegistered from '@/components/compliances/BusinessEntity/BusinessEntityIsVatRegistered.vue'
import {
  commonCompanyName,
  deTaxNumber,
} from '@/components/compliances/BusinessEntity/Validations/BusinessEntityValidationRules'
import { useValidatorWithGraphQlError } from '@/composables/errors'
import { $t } from '@/composables/translation'
import { setdefault } from '@/utils/tools'

export default {
  components: {
    BusinessEntityInput,
    BusinessEntityIsVatRegistered,
  },
  props: {
    businessEntity: {
      type: Object,
      required: true,
    },
    errorsGraphql: {
      type: Object,
      default() {
        return null
      },
    },
    // Staffing agencies must provide a company registration number; companies
    // may leave it blank, so it's opt-in.
    requireCompanyNo: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:businessEntity'],
  setup(props, { emit }) {
    const v$ = useVuelidate()
    const errors = ref(props.errorsGraphql)

    const internalBusinessEntity = ref(props.businessEntity)

    const companyName = computed({
      get() {
        return internalBusinessEntity.value.companyName
      },
      set(value) {
        internalBusinessEntity.value.companyName = value
        v$.value.companyName.$touch()
        emit('update:businessEntity', internalBusinessEntity.value)
      },
    })
    const errorCompanyName = useValidatorWithGraphQlError(
      v$,
      errors,
      'companyName',
      'registered_company_name',
    )

    const companyNo = computed({
      get() {
        return internalBusinessEntity.value.companyNo
      },
      set(value) {
        internalBusinessEntity.value.companyNo = value
        v$.value.companyNo.$touch()
        emit('update:businessEntity', internalBusinessEntity.value)
      },
    })
    const errorCompanyNo = useValidatorWithGraphQlError(v$, errors, 'companyNo', 'company_no')

    const isVatSubject = computed({
      get() {
        return internalBusinessEntity.value.data?.is_vat_subject
      },
      set(value) {
        const data = setdefault(internalBusinessEntity.value, 'data', {})
        data.is_vat_subject = value
        emit('update:businessEntity', internalBusinessEntity.value)
      },
    })

    const vatNo = computed({
      get() {
        return internalBusinessEntity.value.taxNo
      },
      set(value) {
        internalBusinessEntity.value.taxNo = value
        v$.value.vatNo.$touch()
        emit('update:businessEntity', internalBusinessEntity.value)
      },
    })
    const errorVatNo = useValidatorWithGraphQlError(v$, errors, 'vatNo', 'vat_no')

    return {
      companyName,
      companyNo,
      isVatSubject,
      vatNo,
      errorCompanyName,
      errorCompanyNo,
      errorVatNo,
      internalBusinessEntity,
      v$,
    }
  },

  validations() {
    return {
      companyName: commonCompanyName(),
      companyNo: this.requireCompanyNo
        ? { required: helpers.withMessage($t('Company registration number is required'), required) }
        : {},
      vatNo: deTaxNumber(),
    }
  },
}
</script>
